import { useState, type ReactNode } from 'react';
import {
  CalendarBlank,
  CaretDown,
  CaretRight,
  ChatCircle,
  Clock,
  Microphone,
  SpinnerGap,
  UserCircle,
  X,
  Warning as WarningIcon,
  Sparkle as SparkleIcon,
} from '@phosphor-icons/react';
import {
  useSessionDetail,
  type CoachingThread,
  type TranscriptSegment,
} from '@/lib/sessions/useSessionDetail';
import { RescheduleSessionDialog } from '@/components/sessions/RescheduleSessionDialog';
import type { NeuraxInterview } from '@/lib/interviews/useInterviews';
import { cn } from '@/lib/utils/cn';

interface Props {
  sessionId: string | null;
  session?: NeuraxInterview | null;
  onClose: () => void;
}

type Tab = 'transcript' | 'coaching';

export function SessionDetailDialog({ sessionId, session, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('transcript');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  // The one-time reschedule flips eligibility immediately; the session prop is
  // a snapshot from the (now-invalidated) list, so track it locally too.
  const [justRescheduled, setJustRescheduled] = useState(false);
  const detail = useSessionDetail(sessionId);

  if (!sessionId) return null;

  const transcript = detail.data?.transcript ?? null;
  const coaching = detail.data?.coachingThreads ?? [];
  const isTest = session?.session_type === 'online_assessment';
  const titleLabel = isTest ? 'Test session' : 'Interview session';

  const isSetup =
    session?.session_type === 'interview' || session?.session_type === 'online_assessment';
  const wasRescheduled = Boolean(session?.rescheduled_at) || justRescheduled;
  const canReschedule = isSetup && Boolean(session?.interview_date) && !wasRescheduled;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Session details"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-neurax-midnight shadow-glow">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/50 via-neurax-plasma/50 to-transparent" />

        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-white/5 px-6 py-5">
          <div className="flex items-start gap-3 min-w-0">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric">
              {isTest ? <SparkleIcon className="h-5 w-5" /> : <Microphone className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-neurax-electric/80">
                {titleLabel}
              </p>
              <h2 className="font-display text-lg font-semibold text-white truncate">
                {session?.end_client?.name || 'Candidate session'}
              </h2>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {[session?.position_title, session?.company_name].filter(Boolean).join(' · ') ||
                  '—'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canReschedule && (
              <button
                type="button"
                data-testid="reschedule-open"
                onClick={() => setRescheduleOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-white/[0.07]"
              >
                <CalendarBlank className="h-3.5 w-3.5" />
                Reschedule
              </button>
            )}
            {wasRescheduled && (
              <span
                data-testid="rescheduled-chip"
                title={
                  session?.original_interview_date
                    ? `Originally ${new Date(session.original_interview_date).toLocaleString()}`
                    : 'Rescheduled once'
                }
                className="inline-flex items-center rounded-full border border-neurax-amber/30 bg-neurax-amber/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-neurax-amber"
              >
                Rescheduled
              </span>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/5 bg-neurax-ink/40 px-6 py-2">
          <TabButton active={tab === 'transcript'} onClick={() => setTab('transcript')}>
            <Microphone className="h-3.5 w-3.5" />
            Transcript
            {transcript && (
              <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/10 px-1 text-[10px]">
                {transcript.segments.length}
              </span>
            )}
          </TabButton>
          <TabButton active={tab === 'coaching'} onClick={() => setTab('coaching')}>
            <ChatCircle className="h-3.5 w-3.5" />
            Coaching
            {coaching.length > 0 && (
              <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/10 px-1 text-[10px]">
                {coaching.length}
              </span>
            )}
          </TabButton>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
          {detail.isLoading && <LoadingState />}

          {detail.isError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              <WarningIcon className="mt-0.5 h-3.5 w-3.5" />
              <span>{(detail.error as Error)?.message || 'Failed to load session details.'}</span>
            </div>
          )}

          {!detail.isLoading && !detail.isError && (
            <>
              {tab === 'transcript' &&
                (transcript ? (
                  <TranscriptView
                    segments={transcript.segments}
                    interviewerCount={transcript.interviewerSegments}
                    candidateCount={transcript.candidateSegments}
                    syncedAt={transcript.syncedAt}
                  />
                ) : (
                  <EmptyState
                    icon={<Microphone className="h-5 w-5" />}
                    title="Transcript will appear here"
                    description="Once the candidate completes this session in the NeuraX desktop app, the full transcript syncs automatically."
                  />
                ))}

              {tab === 'coaching' &&
                (coaching.length > 0 ? (
                  <CoachingView threads={coaching} />
                ) : (
                  <EmptyState
                    icon={<ChatCircle className="h-5 w-5" />}
                    title="No coaching threads yet"
                    description="Real-time AI coaching prompts the candidate received during their session will show up here."
                  />
                ))}
            </>
          )}
        </div>
      </div>

      {rescheduleOpen && session && (
        <RescheduleSessionDialog
          session={session}
          onClose={() => setRescheduleOpen(false)}
          onRescheduled={() => setJustRescheduled(true)}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Tabs ───────────────────────── */
function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-white/10 text-white'
          : 'text-muted-foreground hover:bg-white/5 hover:text-white'
      )}
    >
      {children}
    </button>
  );
}

/* ───────────────────────── Transcript ───────────────────────── */
function TranscriptView({
  segments,
  interviewerCount,
  candidateCount,
  syncedAt,
}: {
  segments: TranscriptSegment[];
  interviewerCount: number;
  candidateCount: number;
  syncedAt: string | null;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total" value={String(segments.length)} />
        <Stat label="Interviewer" value={String(interviewerCount)} />
        <Stat label="Candidate" value={String(candidateCount)} />
        <Stat label="Synced" value={syncedAt ? fmtDate(syncedAt) : '—'} />
      </div>

      <ul data-testid="transcript-list" className="space-y-2.5 pt-2">
        {segments.map((s) => (
          <SegmentBubble key={s.id} segment={s} />
        ))}
      </ul>
    </div>
  );
}

function SegmentBubble({ segment }: { segment: TranscriptSegment }) {
  const isInterviewer = segment.speaker === 'interviewer';
  return (
    <li
      data-testid={`segment-${segment.id}`}
      className={cn(
        'rounded-2xl border px-4 py-3',
        isInterviewer
          ? 'border-neurax-electric/20 bg-neurax-electric/[0.04]'
          : 'border-neurax-mint/20 bg-neurax-mint/[0.04]'
      )}
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className={cn(
            'inline-flex h-5 items-center gap-1 rounded-full border px-2 text-[10px] font-medium uppercase tracking-wider',
            isInterviewer
              ? 'border-neurax-electric/40 bg-neurax-electric/10 text-neurax-electric'
              : 'border-neurax-mint/40 bg-neurax-mint/10 text-neurax-mint'
          )}
        >
          {isInterviewer ? <UserCircle className="h-3 w-3" /> : <Microphone className="h-3 w-3" />}
          {isInterviewer ? 'Interviewer' : 'Candidate'}
        </span>
        <span className="text-[11px] text-muted-foreground">{fmtTimestamp(segment.timestamp)}</span>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-white">{segment.text}</p>
    </li>
  );
}

/* ───────────────────────── Coaching ───────────────────────── */
function CoachingView({ threads }: { threads: CoachingThread[] }) {
  return (
    <div className="space-y-2.5">
      {threads.map((t, i) => (
        <CoachingCard key={t.id} thread={t} defaultOpen={i === 0} />
      ))}
    </div>
  );
}

function CoachingCard({ thread, defaultOpen }: { thread: CoachingThread; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const userCount = thread.messages.filter((m) => m.role === 'user').length;
  const aiCount = thread.messages.filter((m) => m.role === 'assistant').length;

  // Sanitize the title — desktop app sometimes stores the system prompt as the title
  const cleanTitle = sanitizeTitle(thread.title);

  return (
    <div
      data-testid={`coaching-card-${thread.id}`}
      className="rounded-2xl border border-white/10 bg-white/[0.02]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-white/[0.03]"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <CaretDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <CaretRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="truncate text-sm font-medium text-white">{cleanTitle}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          {fmtDate(thread.createdAt)}
          <span className="text-muted-foreground/40">·</span>
          <span data-testid={`coaching-count-${thread.id}`}>
            {userCount} questions · {aiCount} replies
          </span>
        </div>
      </button>

      {open && (
        <div className="space-y-2 border-t border-white/5 bg-black/20 px-4 py-3">
          {thread.messages.length === 0 ? (
            <p className="text-xs text-muted-foreground">No messages in this thread yet.</p>
          ) : (
            thread.messages.map((m) => <CoachingMessageRow key={m.id} message={m} />)
          )}
        </div>
      )}
    </div>
  );
}

function CoachingMessageRow({
  message,
}: {
  message: { id: string; role: string; content: string; createdAt: string };
}) {
  const isAI = message.role === 'assistant';
  return (
    <div
      data-testid={`coaching-message-${message.id}`}
      className={cn(
        'rounded-xl border px-3 py-2.5',
        isAI
          ? 'border-neurax-plasma/20 bg-neurax-plasma/[0.04]'
          : 'border-neurax-electric/20 bg-neurax-electric/[0.04]'
      )}
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className={cn(
            'inline-flex h-5 items-center rounded-full border px-2 text-[10px] font-medium uppercase tracking-wider',
            isAI
              ? 'border-neurax-plasma/40 bg-neurax-plasma/10 text-neurax-plasma'
              : 'border-neurax-electric/40 bg-neurax-electric/10 text-neurax-electric'
          )}
        >
          {isAI ? 'AI' : 'You'}
        </span>
        <span className="text-[11px] text-muted-foreground">{fmtDateTime(message.createdAt)}</span>
      </div>
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-white">{message.content}</p>
    </div>
  );
}

/* ───────────────────────── Bits ───────────────────────── */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm text-white">{value}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
      <SpinnerGap className="h-5 w-5 animate-spin text-neurax-electric" />
      <p className="text-xs">Loading session details…</p>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div
      className="flex flex-col items-center gap-3 py-10 text-center"
      data-testid="session-empty-state"
    >
      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/[0.04] text-neurax-electric">
        {icon}
      </div>
      <p className="font-display text-base font-semibold text-white">{title}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

/* ───────────────────────── Helpers ───────────────────────── */
function fmtTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function sanitizeTitle(title: string): string {
  if (!title) return 'Untitled thread';
  // Desktop app sometimes stores a system prompt as the title — collapse to first 80 chars
  if (title.length > 80) return title.slice(0, 80) + '…';
  // Also drop common prompt prefixes
  const cleaned = title.replace(/^You are NeuraX[^.]*\.?\s*/, '').trim();
  return cleaned.length > 0 ? cleaned : title;
}
